import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Image from '../../../components/AppImage';

const CreateWishlistModal = ({ isOpen, onClose, onCreateWishlist }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    privacy: 'private',
    occasion: '',
    coverImage: '',
    collaborators: [],
    tags: []
  });
  const [currentTag, setCurrentTag] = useState('');
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const privacyOptions = [
    {
      value: 'private',
      label: 'Private',
      description: 'Only you can see this wishlist',
      icon: 'Lock',
      color: 'text-accent-600'
    },
    {
      value: 'family',
      label: 'Family',
      description: 'Share with family members',
      icon: 'Home',
      color: 'text-warning-600'
    },
    {
      value: 'friends',
      label: 'Friends',
      description: 'Share with your friends',
      icon: 'Users',
      color: 'text-secondary-600'
    },
    {
      value: 'public',
      label: 'Public',
      description: 'Anyone can discover this wishlist',
      icon: 'Globe',
      color: 'text-success-600'
    }
  ];

  const occasionOptions = [
    { value: '', label: 'No specific occasion' },
    { value: 'birthday', label: 'Birthday' },
    { value: 'christmas', label: 'Christmas' },
    { value: 'anniversary', label: 'Anniversary' },
    { value: 'wedding', label: 'Wedding' },
    { value: 'graduation', label: 'Graduation' },
    { value: 'baby-shower', label: 'Baby Shower' },
    { value: 'housewarming', label: 'Housewarming' },
    { value: 'valentine', label: "Valentine's Day" },
    { value: 'mother-day', label: "Mother's Day" },
    { value: 'father-day', label: "Father's Day" },
    { value: 'other', label: 'Other' }
  ];

  const coverImageOptions = [
    'https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=400',
    'https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=400',
    'https://images.unsplash.com/photo-1607344645866-009c7d0f2e8d?w=400',
    'https://images.unsplash.com/photo-1606041008023-472dfb5e530f?w=400',
    'https://images.unsplash.com/photo-1582142306909-195724d33d6c?w=400',
    'https://images.unsplash.com/photo-1606107557195-0e29a4b5b4aa?w=400'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddTag = () => {
    if (currentTag.trim() && !formData.tags.includes(currentTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, currentTag.trim()]
      }));
      setCurrentTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Wishlist name is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // For now, we'll only send name and description to match the current Django API
      // TODO: Extend the API to support coverImage, privacy, occasion, and tags
      await onCreateWishlist({
        name: formData.name.trim(),
        description: formData.description.trim()
      });
      handleClose();
    } catch (err) {
      console.error('Error creating wishlist:', err);

      // More detailed error handling
      let errorMessage = 'Failed to create wishlist. Please try again.';

      if (err.response?.status === 401) {
        errorMessage = 'Please log in to create a wishlist.';
      } else if (err.response?.data) {
        // Handle different error response formats
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.name && Array.isArray(err.response.data.name)) {
          errorMessage = err.response.data.name[0];
        } else if (err.response.data.detail) {
          errorMessage = err.response.data.detail;
        } else if (err.response.data.non_field_errors && Array.isArray(err.response.data.non_field_errors)) {
          errorMessage = err.response.data.non_field_errors[0];
        }
      }

      // Ensure we have a valid error message
      if (!errorMessage || typeof errorMessage !== 'string' || errorMessage.trim().length === 0) {
        errorMessage = 'Failed to create wishlist. Please try again.';
      }

      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      privacy: 'private',
      occasion: '',
      coverImage: '',
      collaborators: [],
      tags: []
    });
    setCurrentTag('');
    setStep(1);
    setError(null);
    setIsSubmitting(false);
    onClose();
  };

  const canProceedToStep2 = formData.name.trim().length > 0;
  const canSubmit = formData.name.trim().length > 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-accent-100">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-secondary-400 rounded-xl flex items-center justify-center">
              <Icon name="Plus" size={20} color="white" />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-xl text-accent-800">
                Create New Wishlist
              </h2>
              <p className="text-sm text-accent-600">
                Step {step} of 2 - {step === 1 ? 'Basic Information' : 'Customization'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-accent-400 hover:text-accent-600 transition-colors"
            disabled={isSubmitting}
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-2">
          <div className="w-full bg-accent-100 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-primary to-secondary h-2 rounded-full transition-all duration-300"
              style={{ width: `${(step / 2) * 100}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Error Message */}
          {error && (
            <div className="p-4 bg-error-50 border border-error-200 rounded-xl mb-6">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} className="text-error-600" />
                <span className="text-sm text-error-700">{error}</span>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div>
                <label className="block text-sm font-medium text-accent-700 mb-2">
                  Wishlist Name *
                </label>
                <Input
                  type="text"
                  placeholder="e.g., Birthday 2024, Dream Home, Travel Essentials"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-accent-700 mb-2">
                  Description
                </label>
                <textarea
                  placeholder="Tell others what this wishlist is about..."
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={3}
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 bg-surface border-0 rounded-xl focus:ring-2 focus:ring-primary/20 resize-none"
                />
              </div>

              {/* Privacy Settings */}
              <div>
                <label className="block text-sm font-medium text-accent-700 mb-3">
                  Privacy Settings
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {privacyOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleInputChange('privacy', option.value)}
                      disabled={isSubmitting}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${formData.privacy === option.value
                          ? 'border-primary bg-primary-50' : 'border-accent-200 hover:border-accent-300'
                        } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className="flex items-center space-x-3 mb-2">
                        <Icon
                          name={option.icon}
                          size={20}
                          className={formData.privacy === option.value ? 'text-primary' : option.color}
                        />
                        <span className={`font-medium ${formData.privacy === option.value ? 'text-primary' : 'text-accent-800'
                          }`}>
                          {option.label}
                        </span>
                      </div>
                      <p className="text-sm text-accent-600">
                        {option.description}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Occasion */}
              <div>
                <label className="block text-sm font-medium text-accent-700 mb-2">
                  Associated Occasion
                </label>
                <select
                  value={formData.occasion}
                  onChange={(e) => handleInputChange('occasion', e.target.value)}
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 bg-surface border-0 rounded-xl focus:ring-2 focus:ring-primary/20"
                >
                  {occasionOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              {/* Cover Image */}
              <div>
                <label className="block text-sm font-medium text-accent-700 mb-3">
                  Choose Cover Image
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {coverImageOptions.map((imageUrl, index) => (
                    <button
                      key={index}
                      onClick={() => handleInputChange('coverImage', imageUrl)}
                      disabled={isSubmitting}
                      className={`relative aspect-video rounded-xl overflow-hidden border-2 transition-all ${formData.coverImage === imageUrl
                          ? 'border-primary shadow-primary'
                          : 'border-accent-200 hover:border-accent-300'
                        } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <Image
                        src={imageUrl}
                        alt={`Cover option ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      {formData.coverImage === imageUrl && (
                        <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                          <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                            <Icon name="Check" size={16} color="white" />
                          </div>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-accent-700 mb-2">
                  Tags
                </label>
                <div className="flex space-x-2 mb-3">
                  <Input
                    type="text"
                    placeholder="Add a tag..."
                    value={currentTag}
                    onChange={(e) => setCurrentTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                    disabled={isSubmitting}
                    className="flex-1"
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleAddTag}
                    disabled={!currentTag.trim() || isSubmitting}
                  >
                    Add
                  </Button>
                </div>
                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.tags.map((tag, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-1 px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm"
                      >
                        <span>{tag}</span>
                        <button
                          onClick={() => handleRemoveTag(tag)}
                          disabled={isSubmitting}
                          className="text-primary-600 hover:text-primary-800 disabled:opacity-50"
                        >
                          <Icon name="X" size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Preview */}
              <div>
                <label className="block text-sm font-medium text-accent-700 mb-3">
                  Preview
                </label>
                <div className="bg-surface rounded-xl p-4">
                  <div className="flex items-center space-x-4">
                    {formData.coverImage && (
                      <div className="w-16 h-16 rounded-lg overflow-hidden">
                        <Image
                          src={formData.coverImage}
                          alt="Cover preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="font-medium text-accent-800">
                        {formData.name || 'Untitled Wishlist'}
                      </h3>
                      {formData.description && (
                        <p className="text-sm text-accent-600 mt-1">
                          {formData.description}
                        </p>
                      )}
                      <div className="flex items-center space-x-2 mt-2">
                        <div className="flex items-center space-x-1 text-xs text-accent-500">
                          <Icon name={privacyOptions.find(p => p.value === formData.privacy)?.icon} size={12} />
                          <span className="capitalize">{formData.privacy}</span>
                        </div>
                        {formData.occasion && (
                          <div className="flex items-center space-x-1 text-xs text-accent-500">
                            <Icon name="Calendar" size={12} />
                            <span>{occasionOptions.find(o => o.value === formData.occasion)?.label}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-accent-100">
          <div className="flex space-x-3">
            {step > 1 && (
              <Button
                variant="ghost"
                onClick={() => setStep(step - 1)}
                disabled={isSubmitting}
                iconName="ChevronLeft"
                iconPosition="left"
              >
                Back
              </Button>
            )}
          </div>

          <div className="flex space-x-3">
            <Button
              variant="ghost"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            {step < 2 ? (
              <Button
                variant="primary"
                onClick={() => setStep(2)}
                disabled={!canProceedToStep2 || isSubmitting}
                iconName="ChevronRight"
                iconPosition="right"
              >
                Next
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={!canSubmit || isSubmitting}
                loading={isSubmitting}
                iconName={isSubmitting ? null : "Check"}
                iconPosition="left"
              >
                {isSubmitting ? 'Creating...' : 'Create Wishlist'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateWishlistModal;